import BaseService from 'vue-core/services/BaseService';
import env from 'environment';

export default class BaseAnalysisService
    extends BaseService {
  constructor({ analysisType, requireAnalysisTypeId }) {
    super();

    if (!analysisType) {
      throw Error('Missing parameter: analysisType');
    }

    this.baseUrl = env.baseUrl + '/analyses';
    this.analysisType = analysisType;
    this.requireAnalysisTypeId = requireAnalysisTypeId;
  }

  clone({ analysisId, originalId }) {
    if (!analysisId) {
      throw Error('Missing parameter: analysisId');
    }
    if (!originalId) {
      throw Error('Missing parameter: originalId');
    }
    return this.get({
      url: `${this.baseUrl}/${analysisId}/${this.analysisType}/clone/${originalId}`
    });
  }

  create({ analysisId }) {
    if (!analysisId) {
      throw Error('Missing parameter: analysisId');
    }
    return this.get({
      url: `${this.baseUrl}/${analysisId}/${this.analysisType}/new`
    });
  }

  find({ analysisId, parameters, pageRequest, sort }) {
    if (!analysisId) {
      throw Error('Missing parameter: analysisId');
    }
    return this.get({
      url: `${this.baseUrl}/${analysisId}/${this.analysisType}`,
      queryParams: [
        parameters,
        pageRequest
          ? { page: pageRequest.number - 1, size: pageRequest.size }
          : { page: 0, size: 0 },
        sort
          ? { sort: sort.sort, order: sort.order.toUpperCase() }
          : {}
      ]
    });
  }

  load({ analysisId, id }) {
    if (!analysisId) {
      throw Error('Missing parameter: analysisId');
    }
    if (!id) {
      throw Error('Missing parameter: id');
    }
    return this.get({
      url: `${this.baseUrl}/${analysisId}/${this.analysisType}/${id}`
    });
  }

  insert({ analysisId, instance }) {
    if (!analysisId) {
      throw Error('Missing parameter: analysisId');
    }
    if (!instance) {
      throw Error('Missing parameter: instance');
    }
    if (instance.id) {
      throw Error('You should not insert an instance with a defined id');
    }
    return this.post({
      url: `${this.baseUrl}/${analysisId}/${this.analysisType}`,
      data: instance
    });
  }

  update({ analysisId, id, instance }) {
    if (!analysisId) {
      throw Error('Missing parameter: analysisId');
    }
    if (!instance) {
      throw Error('Missing parameter: instance');
    }
    return this.put({
      url: `${this.baseUrl}/${analysisId}/${this.analysisType}/${id}`,
      data: instance
    });
  }

  remove({ analysisId, id }) {
    if (!analysisId) {
      throw Error('Missing parameter: analysisId');
    }
    if (!id) {
      throw Error('Missing parameter: id');
    }
    return this.delete({
      url: `${this.baseUrl}/${analysisId}/${this.analysisType}/${id}`,
    });
  }

  uploadFile({ analysisId, tempId, file, progress }) {
    if (!analysisId) {
      throw Error('Missing parameter: analysisId');
    }
    if (!tempId) {
      throw Error('Missing parameter: tempId');
    }
    if (!file) {
      throw Error('Missing parameter: file');
    }

    return this.upload({
      url: `${this.baseUrl}/${analysisId}/${this.analysisType}/${tempId}/upload`,
      data: file,
      progress: progress,
    });
  }

  download({ analysisId, tempId, filename }) {
    if (!analysisId) {
      throw Error('Missing parameter: analysisId');
    }
    if (!tempId) {
      throw Error('Missing parameter: tempId');
    }
    if (!filename) {
      throw Error('Missing parameter: filename');
    }

    return this.get({
      url: `${this.baseUrl}/${analysisId}/${this.analysisType}/${tempId}/${filename}`,
      blob: true
    });
  }

  removeFile({ analysisId, id, tempId, filename }) {
    if (!analysisId) {
      throw Error('Missing parameter: analysisId');
    }
    if (!id) {
      throw Error('Missing parameter: id');
    }
    if (!tempId) {
      throw Error('Missing parameter: tempId');
    }
    if (!filename) {
      throw Error('Missing parameter: filename');
    }

    return this.delete({
      url: `${this.baseUrl}/${analysisId}/${this.analysisType}/${id}/${tempId}/${filename}`,
    });
  }

  processInput(input) {
    return input;
  }

  extractData({ analysisId, tempId, files, adapt, forceProcessing }) {
    if (!analysisId) {
      throw Error('Missing parameter: analysisId');
    }
    if (!tempId) {
      throw Error('Missing parameter: tempId');
    }

    let url = `${this.baseUrl}/${analysisId}/${this.analysisType}/${tempId}`;

    let args = "";
    if (adapt !== undefined && adapt !== null) {
      args += '&adapt=' + (adapt ? 'true' : 'false');
    }
    if (forceProcessing !== undefined && forceProcessing !== null) {
      args += '&force=' + (forceProcessing ? 'true' : 'false');
    }
    if (args) {
      url += '?' + args.substring(1);
    }

    return this.post({
      url,
      data: files,
    });
  }

  exportData({ analysisId, tempId, files, type, adapt, forceProcessing }) {
    if (!analysisId) {
      throw Error('Missing parameter: analysisId');
    }
    if (!tempId) {
      throw Error('Missing parameter: tempId');
    }
    if (!type) {
      throw Error('Missing parameter: type');
    }

    let url = `${this.baseUrl}/${analysisId}/${this.analysisType}/${tempId}?export=${type}`;
    if (adapt !== undefined && adapt !== null) {
      url += '&adapt=' + (adapt ? 'true' : 'false');
    }
    if (forceProcessing !== undefined && forceProcessing !== null) {
      url += '&force=' + (forceProcessing ? 'true' : 'false');
    }

    return this.post({
      url,
      blob: true,
      data: files,
    });
  }

  goBackToExtraction({ analysisId, tempId, source }) {
    if (!analysisId) {
      throw Error('Missing parameter: analysisId');
    }
    if (!tempId) {
      throw Error('Missing parameter: tempId');
    }
    if (!source) {
      throw Error('Missing parameter: source');
    }

    let url = `${this.baseUrl}/${analysisId}/${this.analysisType}/${tempId}/extraction/${source}`;

    return this.get({
      url
    });
  }

  analyze({ analysisId, tempId, analysisTypeId, input, includeInput, forceProcessing }) {
    if (!analysisId) {
      throw Error('Missing parameter: analysisId');
    }
    if (!tempId) {
      throw Error('Missing parameter: tempId');
    }
    if (this.requireAnalysisTypeId && !analysisTypeId) {
      throw Error('Missing parameter: analysisTypeId');
    }

    let url = this.requireAnalysisTypeId
            ? `${this.baseUrl}/${analysisId}/${this.analysisType}/${tempId}/${analysisTypeId}`
            : `${this.baseUrl}/${analysisId}/${this.analysisType}/${tempId}/analyze`;

    let args = "";
    if (includeInput !== undefined && includeInput !== null) {
      args += '&input=' + (includeInput ? 'true' : 'false');
    }
    if (forceProcessing !== undefined && forceProcessing !== null) {
      args += '&force=' + (forceProcessing ? 'true' : 'false');
    }
    if (args) {
      url += '?' + args.substring(1);
    }

    return this.post({
      url,
      data: this.processInput(input),
    });
  }

  exportAnalysis({ analysisId, tempId, analysisTypeId, input, includeInput, type }) {
    if (!analysisId) {
      throw Error('Missing parameter: analysisId');
    }
    if (!tempId) {
      throw Error('Missing parameter: tempId');
    }
    if (this.requireAnalysisTypeId && !analysisTypeId) {
      throw Error('Missing parameter: analysisTypeId');
    }
    if (!type) {
      throw Error('Missing parameter: type');
    }

    let url = this.requireAnalysisTypeId
            ? `${this.baseUrl}/${analysisId}/${this.analysisType}/${tempId}/${analysisTypeId}?export=${type}`
            : `${this.baseUrl}/${analysisId}/${this.analysisType}/${tempId}/analyze?export=${type}`;

    if (includeInput !== undefined && includeInput !== null) {
      url += '&input=' + (includeInput ? 'true' : 'false');
    }

    return this.post({
      url,
      blob: true,
      data: this.processInput(input),
    });
  }

  goBackToAnalysis({ analysisId, tempId, source }) {
    if (!analysisId) {
      throw Error('Missing parameter: analysisId');
    }
    if (!tempId) {
      throw Error('Missing parameter: tempId');
    }
    if (!source) {
      throw Error('Missing parameter: source');
    }

    let url = `${this.baseUrl}/${analysisId}/${this.analysisType}/${tempId}/analyses/${source}`;

    return this.get({
      url
    });
  }
}
